import React, { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/toaster";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import LuxurySplash from "./components/LuxurySplash";
import { shouldShowSplash } from "./lib/splash";
import ErrorBoundary from "./components/ErrorBoundary";
import { BOOK_PATH } from "./lib/routes";
import Landing from "./components/Landing";
import Signup from "./components/Signup";
import Login from "./components/Login";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import ConfirmEmail from "./components/ConfirmEmail";
import Dashboard from "./components/Dashboard";
import BookPreview from "./components/BookPreview";
import Profile from "./components/Profile";
import RideHistory from "./components/RideHistory";
import LiveRide from "./components/LiveRide";
import About from "./components/About";
import Contact from "./components/Contact";
import Terms from "./components/Terms";
import Privacy from "./components/Privacy";
import Admin from "./components/Admin";
import DriveWithUs from "./components/DriveWithUs";
import BuraqMiles from "./components/BuraqMiles";
import LostFound from "./components/LostFound";
import SuccessRoute from "./components/SuccessRoute";
import BusinessHome from "./components/BusinessHome";
import BusinessBook from "./components/business/BusinessBook";
import BusinessRides from "./components/BusinessRides";
import Travelers from "./components/Travelers";
import BusinessSettings from "./components/BusinessSettings";
import BusinessInvite from "./components/BusinessInvite";
import ThankYou from "./components/ThankYou";
import ProtectedRoute from "./components/ProtectedRoute";

// An old link keeps working and keeps its query (a Stripe return, a shared link).
function RedirectKeepQuery({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

// A crash on a page shows a friendly message instead of a blank page, and clears when the visitor moves to another page.
function RouteBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}

function App() {
  const [showSplash, setShowSplash] = useState(() => shouldShowSplash());     // once per visit, never on a route change

  useEffect(() => {
    // A previous cache-first service worker caused stale/blank pages after redeploys.
    // Unregister any existing SW and clear its caches so returning devices self-heal.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
      }).catch(() => {});
      if (window.caches && caches.keys) {
        caches.keys().then((names) => names.forEach((n) => caches.delete(n))).catch(() => {});
      }
    }
  }, []);

  return (
    <div className="App min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <RouteBoundary>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/drive" element={<DriveWithUs />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/business" element={<ProtectedRoute><BusinessHome /></ProtectedRoute>} />
            <Route path="/business/book" element={<ProtectedRoute><BusinessBook /></ProtectedRoute>} />
            <Route path="/business/rides" element={<ProtectedRoute><BusinessRides /></ProtectedRoute>} />
            <Route path="/business/travelers" element={<ProtectedRoute><Travelers /></ProtectedRoute>} />
            <Route path="/business/invite" element={<BusinessInvite />} />
            <Route path="/business/settings" element={<ProtectedRoute><BusinessSettings /></ProtectedRoute>} />
            <Route
              path="/miles"
              element={
                <ProtectedRoute>
                  <BuraqMiles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lost-found"
              element={
                <ProtectedRoute>
                  <LostFound />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ride/:reference"
              element={
                <ProtectedRoute>
                  <LiveRide />
                </ProtectedRoute>
              }
            />
            <Route path="/payment-setup" element={<Navigate to="/dashboard" replace />} />
            <Route 
              path="/success" 
              element={<SuccessRoute />} 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path={BOOK_PATH}
              element={
                <ProtectedRoute>
                  <BookPreview />
                </ProtectedRoute>
              }
            />
            <Route path="/book-ride" element={<RedirectKeepQuery to={BOOK_PATH} />} />
            <Route path="/book-preview" element={<RedirectKeepQuery to={BOOK_PATH} />} />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rides" 
              element={
                <ProtectedRoute>
                  <RideHistory />
                </ProtectedRoute>
              } 
            />
          </Routes>
          </RouteBoundary>
          <Toaster />
          <PWAInstallPrompt />
        </BrowserRouter>
      </AuthProvider>
      </ErrorBoundary>
      {showSplash && <LuxurySplash onDone={() => setShowSplash(false)} />}
    </div>
  );
}

export default App;